import classNames from 'classnames';
import React, { ReactElement } from 'react';

import { ICON_COLOR, ICON_SIZE, ICON_TYPE, OzIcon } from '../Icon/Icon.component';

import { IDeviceOptions, IPaginationProps } from './Pagination.interfaces';
import { paginationDisplayName } from './Pagination.qa';
import { getPaginationLabels } from './Pagination.utils';
import { PaginationPages } from './PaginationPages.component';

export const DeviceOption: IDeviceOptions = {
    DESKTOP: {
        pageLimit: 14,
        maxAllowedPerSide: 5,
    },
    MOBILE: {
        pageLimit: 5,
        maxAllowedPerSide: 1,
    },
};

export enum Separator {
    ELLIPSIS = '...'
}

export function OzPagination (props: IPaginationProps): ReactElement {
    const {
        totalPages,
        currentPage = 1,
        onChange,
        className = '',
        separator = Separator.ELLIPSIS,
        pagesClassName: paginationPagesClassName = '',
        'data-testid': dataTestId = '',
    }: IPaginationProps = props;

    function handlePreviousClick (): void {
        let page = currentPage - 1;

        if (page < 1) {
            page = 1;
        }

        onChange(page);
    }

    function handleNextClick (): void {
        let page = currentPage + 1;

        if (page > totalPages) {
            page = totalPages;
        }

        onChange(page);
    }

    const previousWrapperClassName: string = classNames({
        'invisible': (currentPage <= 1),
    });

    const nextWrapperClassName: string = classNames({
        'invisible': (currentPage >= totalPages),
    });

    const pages: string[] = getPaginationLabels({
        totalPages: totalPages,
        currentPage: currentPage,
        separator: separator,
        deviceOption: DeviceOption.DESKTOP,
    });

    const mobilePages: string[] = getPaginationLabels({
        totalPages: totalPages,
        currentPage: currentPage,
        separator: separator,
        deviceOption: DeviceOption.MOBILE,
    });

    const pagesClassName: string = classNames({
        'hidden md:flex md:justify-center': true,
        [paginationPagesClassName]: !!paginationPagesClassName,
    });

    const mobilePagesClassName: string = classNames({
        'flex justify-center md:hidden': true,
        [paginationPagesClassName]: !!paginationPagesClassName,
    });

    return (
        <div
            className={`w-full flex font-normal font-primary justify-between items-center ${className}`}
            data-testid={dataTestId}
        >
            <div className={previousWrapperClassName}>
                <div
                    onClick={handlePreviousClick}
                    className='flex capitalize type-subtitle1 text-primary cursor-pointer'
                >
                    <OzIcon
                        type={ICON_TYPE.OZ_ARROW_RIGHT}
                        className='my-auto mx-1 text-primary transform rotate-180'
                        size={ICON_SIZE.MEDIUM}
                        color={ICON_COLOR.PRIMARY}
                    />
                        previous
                </div>
            </div>

            {/* for tablet screens and above */}
            <PaginationPages
                pages={pages}
                currentPage={currentPage}
                handleChange={onChange}
                separator={separator}
                className={pagesClassName}
                data-testid={`${dataTestId}-pages`}
            />

            {/* for mobile screens only */}
            <PaginationPages
                pages={mobilePages}
                currentPage={currentPage}
                separator={separator}
                handleChange={onChange}
                className={mobilePagesClassName}
                data-testid={`${dataTestId}-mobile-pages`}
            />

            <div className={nextWrapperClassName}>
                <div
                    onClick={handleNextClick}
                    className='justify-end flex capitalize type-subtitle1 text-primary cursor-pointer'
                >
                        next
                    <OzIcon
                        type={ICON_TYPE.OZ_ARROW_RIGHT}
                        className='my-auto mx-1 text-primary'
                        size={ICON_SIZE.MEDIUM}
                        color={ICON_COLOR.PRIMARY}
                    />
                </div>
            </div>
        </div>
    );
}

OzPagination.displayName = paginationDisplayName;
